// Entity Info page hook. Mirrors the JSON shape from
// crates/tres-api/src/handlers/entity.rs.

import { useQueries, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';
import type { TagInput, TagView } from '@/lib/tags';

export interface EntityLabels {
  primary: string;
  pref_label?: string;
  rdfs_label?: string;
  alt_labels?: string[];
}

export interface UriLabel {
  uri: string;
  label: string;
}

/** Derived closed-world validity for a fact. `via_context` is set only when the
 * binding end was INHERITED from the fact's `@context` (not declared on the fact
 * itself) — the "which context made this expire" audit signal. */
export interface ValidityView {
  expired: boolean;
  not_yet_valid?: boolean;
  until?: string;
  via_context?: UriLabel;
}

/** `statement_id` names the stored statement a row shows (QROS only), so a
 *  delete or tag edit on the row leaves the same fact in its other contexts. */
export type PredicateValue =
  | {
      kind: 'uri';
      uri: string;
      label: string;
      tags?: TagView[];
      validity?: ValidityView;
      statement_id?: string;
    }
  | {
      kind: 'literal';
      value: string;
      lang?: string;
      datatype?: string;
      tags?: TagView[];
      validity?: ValidityView;
      statement_id?: string;
    }
  | { kind: 'bnode'; uri: string; tags?: TagView[]; statement_id?: string };

export interface PredicateGroup {
  predicate: UriLabel;
  values: PredicateValue[];
}

export interface InboundPredicate {
  predicate: UriLabel;
  count: number;
  sample: UriLabel[];
}

export interface InboundSummary {
  predicates: InboundPredicate[];
  truncated: boolean;
  rows_seen: number;
}

export interface EntityResponse {
  uri: string;
  graph: string;
  labels: EntityLabels;
  types: UriLabel[];
  outbound: PredicateGroup[];
  /** Graphs this entity has statements in — Edit Mode jumps to the writable scope. */
  home_graphs?: string[];
  // Inbound ("Referenced by") is NOT in this payload — it has no object index on
  // the QROS store (a full-store scan), so it is fetched lazily via
  // useEntityInboundSummaryIn and the indexed outbound/types render immediately.
}

/**
 * Fetch the structured entity page payload for a given URI in a given
 * graph. Disabled until both inputs are non-empty (the page can render
 * a "pick something" empty state while the user searches).
 */
export function useEntity(uri: string | null, graph: string | null) {
  const { token, ready } = useApiSession();
  const enabled = ready && Boolean(uri) && Boolean(graph);

  return useQuery<EntityResponse>({
    queryKey: ['entity', uri ?? 'none', graph ?? 'none', token ?? 'anon'],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      if (!uri || !graph) throw new Error('not ready');
      const url = `/api/v1/entity?uri=${encodeURIComponent(uri)}&graph=${encodeURIComponent(graph)}`;
      const r = await apiFetch<EntityResponse>(url, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

/** What a set of per-graph reads amounts to, in the shape one read has. */
export interface MergedRead<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
}

/** Statement groups from several graphs, one group per predicate. */
function mergeEntities(parts: EntityResponse[]): EntityResponse | undefined {
  const [first] = parts;
  if (!first) return undefined;
  const groups = new Map<string, PredicateGroup>();
  for (const part of parts) {
    for (const group of part.outbound) {
      const have = groups.get(group.predicate.uri);
      if (have) have.values.push(...group.values);
      else groups.set(group.predicate.uri, { predicate: group.predicate, values: [...group.values] });
    }
  }
  const types = new Map<string, UriLabel>();
  for (const part of parts) for (const t of part.types) types.set(t.uri, t);
  const homes = new Set<string>();
  for (const part of parts) for (const g of part.home_graphs ?? []) homes.add(g);
  return {
    ...first,
    graph: parts.map((p) => p.graph).join(','),
    types: [...types.values()],
    outbound: [...groups.values()],
    home_graphs: [...homes],
  };
}

/**
 * The entity as the picked graphs hold it (Ch08 §2.7): one read per graph,
 * merged, so a scope of several graphs reads those graphs and no more. Each
 * read keeps the key a single-graph read has, so an edit's cache update
 * reaches it.
 */
export function useEntityIn(uri: string | null, graphs: string[]): MergedRead<EntityResponse> {
  const { token, ready } = useApiSession();
  const results = useQueries({
    queries: graphs.map((graph) => ({
      queryKey: ['entity', uri ?? 'none', graph, token ?? 'anon'],
      enabled: ready && Boolean(uri),
      staleTime: 30_000,
      queryFn: async () => {
        if (!uri) throw new Error('not ready');
        const url = `/api/v1/entity?uri=${encodeURIComponent(uri)}&graph=${encodeURIComponent(graph)}`;
        const r = await apiFetch<EntityResponse>(url, token);
        if (!r.ok) throw new Error(r.message);
        return r.data;
      },
    })),
  });
  const isLoading = results.some((r) => r.isLoading);
  const error = (results.find((r) => r.error)?.error as Error | undefined) ?? null;
  const parts = results.map((r) => r.data).filter((d): d is EntityResponse => d !== undefined);
  return {
    data: !isLoading && parts.length === graphs.length ? mergeEntities(parts) : undefined,
    isLoading,
    error,
  };
}

/** Inbound summaries from several graphs, counts added per predicate. */
export function useEntityInboundSummaryIn(
  uri: string | null,
  graphs: string[],
): MergedRead<InboundSummary> {
  const { token, ready } = useApiSession();
  const results = useQueries({
    queries: graphs.map((graph) => ({
      queryKey: ['entity-inbound-summary', uri ?? 'none', graph, token ?? 'anon'],
      enabled: ready && Boolean(uri),
      staleTime: 30_000,
      queryFn: async () => {
        if (!uri) throw new Error('not ready');
        const url = `/api/v1/entity/inbound-summary?uri=${encodeURIComponent(uri)}&graph=${encodeURIComponent(graph)}`;
        const r = await apiFetch<InboundSummary>(url, token);
        if (!r.ok) throw new Error(r.message);
        return r.data;
      },
    })),
  });
  const isLoading = results.some((r) => r.isLoading);
  const error = (results.find((r) => r.error)?.error as Error | undefined) ?? null;
  const parts = results.map((r) => r.data).filter((d): d is InboundSummary => d !== undefined);
  let data: InboundSummary | undefined;
  if (!isLoading && parts.length === graphs.length && parts.length > 0) {
    const byPredicate = new Map<string, InboundPredicate>();
    for (const part of parts) {
      for (const p of part.predicates) {
        const have = byPredicate.get(p.predicate.uri);
        if (have) {
          have.count += p.count;
          have.sample = [...have.sample, ...p.sample].slice(0, have.sample.length || p.sample.length);
        } else byPredicate.set(p.predicate.uri, { ...p, sample: [...p.sample] });
      }
    }
    data = {
      predicates: [...byPredicate.values()],
      truncated: parts.some((p) => p.truncated),
      rows_seen: parts.reduce((n, p) => n + p.rows_seen, 0),
    };
  }
  return { data, isLoading, error };
}

/** What links to an entity across the whole database: the statements naming
 *  it, per graph, and those using it as their context. While any remain the
 *  entity is not deleted (77.12). */
export interface EntityReferences {
  graphs: { graph: string; count: number }[];
  as_context: number;
  /** Statements using the entity as their property (77.21). */
  as_property: number;
  truncated: boolean;
}

export function useEntityReferences(uri: string | null) {
  const { token, ready } = useApiSession();
  return useQuery<EntityReferences>({
    queryKey: ['entity-references', uri ?? 'none', token ?? 'anon'],
    enabled: ready && Boolean(uri),
    staleTime: 0,
    queryFn: async () => {
      if (!uri) throw new Error('not ready');
      const r = await apiFetch<EntityReferences>(
        `/api/v1/entity/references?uri=${encodeURIComponent(uri)}`,
        token,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

/**
 * Display labels for a set of terms, from the entity payload in one graph (the
 * union, on a surface with no graph scope). Each read shares the entity page's
 * cache key. A term not yet read, or not found, is absent from the map and the
 * caller shows its local name.
 */
export function useEntityLabels(uris: string[], graph: string): Map<string, string> {
  const { token, ready } = useApiSession();
  const results = useQueries({
    queries: uris.map((uri) => ({
      queryKey: ['entity', uri, graph, token ?? 'anon'],
      enabled: ready,
      staleTime: 30_000,
      queryFn: async (): Promise<EntityResponse> => {
        const url = `/api/v1/entity?uri=${encodeURIComponent(uri)}&graph=${encodeURIComponent(graph)}`;
        const r = await apiFetch<EntityResponse>(url, token);
        if (!r.ok) throw new Error(r.error);
        return r.data;
      },
    })),
  });
  const labels = new Map<string, string>();
  results.forEach((r, i) => {
    const uri = uris[i];
    if (uri && r.data?.labels.primary) labels.set(uri, r.data.labels.primary);
  });
  return labels;
}

// ---- write surface (mint entity, add/retract statement) ----
// Plain async mutation functions the page invokes, then invalidates the
// ['entity'] queries. All gated server-side to Action::ImportData.

/** Object term: exactly one of `uri` or a literal `value`. No blank nodes. */
export interface TermInput {
  uri?: string | undefined;
  value?: string | undefined;
  lang?: string | undefined;
  datatype?: string | undefined;
}

export interface PropertyInputDto {
  predicate: string;
  object: TermInput;
  /** Per-statement override tags (QROS-native; ignored on RDF). */
  tags?: TagInput[];
}

/** Mint a new entity of `class` with optional defining properties; returns
 *  the minted URI. Tags ride per property row (tags attach to statements,
 *  never to entities — there is no block-level tag input). */
export async function mintEntity(
  body: { graph: string; class: string; properties: PropertyInputDto[] },
  token: string,
) {
  return apiFetch<{ uri: string }>('/api/v1/entity', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** What adding a statement did: on a property the ontology holds to one value,
 *  the held value it replaced; on any property, whether the statement was
 *  already held (same property, value and context), so no row was added and
 *  its tags were merged, and which tags that changed. Null (204) when a new
 *  row was added and nothing was replaced. A value older than the one held is
 *  refused (409) with the reason. */
export interface StatementWrite {
  updated: number;
  not_applied_older: number;
  already_held: boolean;
  tags_changed: string[];
}

/** Assert one statement (`subject` in `graph`). `tags` are QROS-native. */
export async function addStatement(
  body: {
    graph: string;
    subject: string;
    predicate: string;
    object: TermInput;
    tags?: TagInput[];
  },
  token: string,
) {
  return apiFetch<StatementWrite | null>('/api/v1/entity/statement', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Replace the tag set on one EXISTING statement (QROS-native). The server
 *  retracts the addressed statement and re-asserts it carrying exactly `tags` —
 *  submitting an empty list clears the statement's editable tags. With
 *  `statement_id`, only that row's statement; without it, the fact in every
 *  context. */
export async function updateStatementTags(
  body: {
    graph: string;
    subject: string;
    predicate: string;
    object: TermInput;
    tags: TagInput[];
    statement_id?: string;
  },
  token: string,
) {
  return apiFetch<null>('/api/v1/entity/statement', token, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Retract one statement: with `statement_id`, that row's statement only;
 *  without it, the fact in every context it holds in. */
export async function retractStatement(
  body: {
    graph: string;
    subject: string;
    predicate: string;
    object: TermInput;
    statement_id?: string;
  },
  token: string,
) {
  return apiFetch<null>('/api/v1/entity/statement', token, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/** Delete an entity: every statement where it is subject or object. */
export async function deleteEntity(body: { graph: string; uri: string }, token: string) {
  return apiFetch<{ removed: number; unmatched: number; inbound_truncated: boolean }>('/api/v1/entity', token, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/** Merge `source` into `target`: re-point all references, then `source` is empty.
 *  Additive — the target keeps its own values and gains the source's. */
export async function mergeEntity(
  body: { graph: string; source: string; target: string },
  token: string,
) {
  return apiFetch<{ moved: number }>('/api/v1/entity/merge', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Set an entity's classes (its `:type` set). Added classes must be known
 *  domain classes; the set may not be emptied. */
export async function setEntityClasses(
  body: { graph: string; uri: string; classes: string[] },
  token: string,
) {
  return apiFetch<{ types: string[] }>('/api/v1/entity/class', token, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export interface InboundSubjectsPage {
  items: UriLabel[];
  total: number;
  offset: number;
  limit: number;
}

/**
 * One page of the full subject list behind an inbound predicate group
 * — backs the "+ NN more" expansion. Previous page stays on screen
 * while the next one loads.
 */
export function useInboundSubjects(
  uri: string,
  graph: string,
  predicate: string,
  offset: number,
  enabled: boolean,
  limit = 25,
) {
  // `enabled` is the caller's — this hook paginates a panel the caller has
  // already decided to show, so it does not re-derive readiness.
  const { token } = useApiSession();

  return useQuery<InboundSubjectsPage>({
    queryKey: ['entity-inbound', uri, graph, predicate, offset, limit, token ?? 'anon'],
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const params = new URLSearchParams({
        uri,
        graph,
        predicate,
        offset: String(offset),
        limit: String(limit),
      });
      const r = await apiFetch<InboundSubjectsPage>(
        `/api/v1/entity/inbound?${params.toString()}`,
        token,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}
